import { useState, useEffect, useCallback, useMemo } from 'react'
import { DriveListItemStatus } from '@/user_files/drive/constants'
import { fileProcessingStatusToDriveStatus } from '@/user_files/utils/drive-list-format'

export interface FileForSelection {
  id: string
  folder_id: string
  original_filename: string
  processing_status?: string | null
}

export interface FolderForSelection {
  id: string
  parent_id: string | null
  path: string
  name: string
}

export function useDriveSelection(files: FileForSelection[], folders: FolderForSelection[], currentFolderId: string | null) {
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([])
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([])

  // Clear selection when folder changes
  useEffect(() => {
    setSelectedFileIds([])
    setSelectedFolderIds([])
  }, [currentFolderId])

  const selectableFileIds = useMemo(
    () =>
      files
        .filter((file) => {
          const rowStatus = fileProcessingStatusToDriveStatus(file.processing_status)
          return rowStatus !== DriveListItemStatus.Processing && rowStatus !== DriveListItemStatus.Pending
        })
        .map((file) => file.id),
    [files]
  )
  const selectableFileIdSet = useMemo(() => new Set(selectableFileIds), [selectableFileIds])
  const selectedSelectableFileIds = useMemo(() => selectedFileIds.filter((fileId) => selectableFileIdSet.has(fileId)), [selectedFileIds, selectableFileIdSet])
  const selectedSelectableFileIdSet = useMemo(() => new Set(selectedSelectableFileIds), [selectedSelectableFileIds])
  const selectedFolderIdSet = useMemo(() => new Set(selectedFolderIds), [selectedFolderIds])

  // Drop files from selection when they become non-selectable (e.g. enter pending/processing)
  useEffect(() => {
    setSelectedFileIds((prev) => prev.filter((fileId) => selectableFileIdSet.has(fileId)))
  }, [selectableFileIdSet])

  const isFileSelected = useCallback((fileId: string) => selectedSelectableFileIdSet.has(fileId), [selectedSelectableFileIdSet])

  const toggleFileSelect = useCallback((fileId: string) => {
    setSelectedFileIds((prev) => (prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]))
  }, [])

  const isFolderSelected = useCallback((folderId: string) => selectedFolderIdSet.has(folderId), [selectedFolderIdSet])

  const toggleFolderSelect = useCallback((folderId: string) => {
    setSelectedFolderIds((prev) => (prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]))
  }, [])

  const selectedCount = selectedSelectableFileIds.length + selectedFolderIds.length

  const isAllSelected = useMemo(() => {
    const total = selectableFileIds.length + folders.length
    if (total === 0) return false
    return selectableFileIds.every((id) => selectedSelectableFileIdSet.has(id)) && folders.every((f) => selectedFolderIdSet.has(f.id))
  }, [selectableFileIds, folders, selectedSelectableFileIdSet, selectedFolderIdSet])

  const selectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedFileIds([])
      setSelectedFolderIds([])
    } else {
      setSelectedFileIds(selectableFileIds)
      setSelectedFolderIds(folders.map((f) => f.id))
    }
  }, [isAllSelected, selectableFileIds, folders])

  const clearSelection = useCallback(() => {
    setSelectedFileIds([])
    setSelectedFolderIds([])
  }, [])

  return {
    selectedSelectableFileIds,
    selectedFolderIds,
    isFileSelected,
    toggleFileSelect,
    isFolderSelected,
    toggleFolderSelect,
    selectedCount,
    isAllSelected,
    selectAll,
    clearSelection,
  }
}
